import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, FILE_URL, formatBRL } from "../../lib/api";
import { Plus, Edit2, Trash2, Upload, FileSpreadsheet, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import StatusQuickEdit from "../../components/admin/StatusQuickEdit";
import BulkStatusUpdateModal from "../../components/admin/BulkStatusUpdateModal";

const PAGE_SIZE = 50;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Raw input (updated on every keystroke) vs the debounced value we actually
  // send to the server. Without the debounce, every typed character would fire
  // a round-trip + count_documents call against 1.3k+ docs.
  const [queryInput, setQueryInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const tableTopRef = useRef(null);

  // 300ms debounce of the search input. Also resets to page 1 — a search on
  // page 3 of a previous result set would otherwise show a confusing empty page.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(queryInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products", { params: { page, limit: PAGE_SIZE, search: search || undefined } });
      setProducts(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      // Server clamps page if it exceeds total_pages — sync our state to avoid a stale "Página 8 de 5".
      if (r.data.page && r.data.page !== page) setPage(r.data.page);
    } catch {
      setProducts([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const removeProduct = async (id) => {
    if (!confirm("Excluir esta peça?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const goToPage = (nextPage) => {
    const clamped = Math.max(1, Math.min(totalPages, nextPage));
    if (clamped === page) return;
    setPage(clamped);
    // Scroll the table back into view so the admin sees the new rows
    // immediately (especially on long pages where the footer pagination is
    // far below the fold).
    if (tableTopRef.current) {
      tableTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // "Exibindo X–Y de N peças" — 1-indexed range for the current page.
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
        <div>
          <p className="overline text-[#A8A8A8] mb-1">Catálogo</p>
          <h1 className="font-display text-4xl">Produtos</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/produtos/importar" data-testid="bulk-import-btn" className="btn-secondary inline-flex items-center">
            <Upload size={14} className="mr-2"/> Cadastro Rápido
          </Link>
          <button
            type="button"
            onClick={() => setBulkStatusOpen(true)}
            data-testid="bulk-status-btn"
            className="btn-secondary inline-flex items-center"
          >
            <FileSpreadsheet size={14} className="mr-2"/> Atualização Rápida
          </button>
          <Link to="/admin/produtos/novo" data-testid="add-product-btn" className="btn-primary inline-flex items-center">
            <Plus size={14} className="mr-2"/> Novo produto
          </Link>
        </div>
      </div>

      <BulkStatusUpdateModal
        open={bulkStatusOpen}
        onClose={() => setBulkStatusOpen(false)}
        onDone={() => load()}
      />

      {/* Search bar — filters by code, name or brand (server-side) */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8A8]" />
          <input
            data-testid="admin-products-search"
            type="text"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Pesquisar por código, nome ou marca…"
            className="input w-full pl-9 pr-9"
          />
          {queryInput && (
            <button
              type="button"
              onClick={() => setQueryInput("")}
              data-testid="admin-products-search-clear"
              aria-label="Limpar pesquisa"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#A8A8A8] hover:text-[#1A1A1A]"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <p className="text-xs text-[#A8A8A8]" data-testid="admin-products-count">
          {loading
            ? "…"
            : total === 0
              ? "0 peças"
              : `Exibindo ${rangeStart}–${rangeEnd} de ${total} ${total === 1 ? "peça" : "peças"}`}
        </p>
      </div>

      <div ref={tableTopRef} />

      {loading ? (
        <p className="text-[#A8A8A8]">Carregando...</p>
      ) : (
        <div className="bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF9F7] text-left">
              <tr>
                <th className="p-4 overline text-[#A8A8A8]">Código</th>
                <th className="p-4 overline text-[#A8A8A8]">Foto</th>
                <th className="p-4 overline text-[#A8A8A8]">Nome</th>
                <th className="p-4 overline text-[#A8A8A8]">Marca</th>
                <th className="p-4 overline text-[#A8A8A8]">Categoria</th>
                <th className="p-4 overline text-[#A8A8A8]">Preço</th>
                <th className="p-4 overline text-[#A8A8A8]">Status</th>
                <th className="p-4 overline text-[#A8A8A8]">Ações</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-[#E8E4DF]" data-testid={`admin-row-${p.id}`}>
                  <td className="p-4 font-mono text-xs">{p.code || "—"}</td>
                  <td className="p-4">
                    <div className="w-12 h-12 bg-[#FAF9F7]">
                      {p.images?.[0] && <img src={FILE_URL(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                  </td>
                  <td className="p-4">{p.name}</td>
                  <td className="p-4 text-[#6B6B6B]">{(p.brand === "Outra" || p.brand === "Outras") && p.custom_brand ? p.custom_brand : p.brand}</td>
                  <td className="p-4 text-[#6B6B6B]">{p.category}</td>
                  <td className="p-4">{formatBRL(p.price)}</td>
                  <td className="p-4">
                    <StatusQuickEdit
                      productId={p.id}
                      status={p.status}
                      onChange={(next) => {
                        // Optimistically reflect the new status in the local list
                        // so the admin can see it without waiting for a refetch.
                        // A reload() would also work but would trigger a full
                        // page refresh and lose the per-row flash animation.
                        setProducts((prev) => prev.map((row) => (row.id === p.id ? { ...row, status: next } : row)));
                      }}
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Link to={`/admin/produtos/${p.id}`} className="p-2 hover:text-[#B8A88A]" data-testid={`edit-${p.id}`}><Edit2 size={14}/></Link>
                      <button onClick={() => removeProduct(p.id)} className="p-2 hover:text-red-600" data-testid={`del-${p.id}`}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total === 0 && !search && (
            <p className="p-8 text-center text-[#A8A8A8]">Nenhum produto. Adicione o primeiro!</p>
          )}
          {total === 0 && search && (
            <p className="p-8 text-center text-[#A8A8A8]" data-testid="admin-products-empty-search">
              Nenhuma peça encontrada para &quot;{search}&quot;.
            </p>
          )}

          {/* Pagination footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-5 border-t border-[#E8E4DF]" data-testid="admin-products-pagination">
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                data-testid="pagination-prev"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] px-3 py-2 border border-[#E8E4DF] hover:border-[#B8A88A] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14}/> Anterior
              </button>
              <p className="text-xs text-[#6B6B6B]" data-testid="pagination-status">
                Página <span className="font-medium text-[#1A1A1A]">{page}</span> de <span className="font-medium text-[#1A1A1A]">{totalPages}</span>
              </p>
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                data-testid="pagination-next"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] px-3 py-2 border border-[#E8E4DF] hover:border-[#B8A88A] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Próxima <ChevronRight size={14}/>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
